import { lazy } from 'react';
import { createBrowserRouter } from 'react-router';
import * as Sentry from '@sentry/react';
import RootLayout from './RootLayout';
import { asetquLandEnabled } from '@/lib/features';

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

// Lazy load wrapper pages (they handle device detection internally)
const HomePage = lazy(() => import('./pages/HomePage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const HistoryPage = lazy(() => import('./pages/HistoryPage'));
const MyAuctionsPage = lazy(() => import('./pages/MyAuctionsPage'));
const MyVouchersPage = lazy(() => import('./pages/MyVouchersPage'));
const MyAsetAcademyPage = lazy(() => import('./pages/MyAsetAcademyPage'));
const PropriboxPage = lazy(() => import('./pages/PropriboxPage'));
const AcademyPageHome = lazy(() => import('./pages/AcademyPageHome'));
const AcademyPage = lazy(() => import('./pages/AcademyPage'));
const AcademyDetailPage = lazy(() => import('./pages/academy/detail/AcademyDetailPage'));
const AcademyResourcePage = lazy(() => import('./pages/academy/resource/AcademyResourcePage'));
const PropribidPage = lazy(() => import('./pages/PropribidPage'));
const PropribidDetailPage = lazy(() => import('./pages/propribid/detail/PropribidDetailPage'));
const ProprimarketPage = lazy(() => import('./pages/ProprimarketPage'));
const ProprimarketDetailPage = lazy(
  () => import('./pages/proprimarket/detail/ProprimarketDetailPage')
);
const ProprivoucherPage = lazy(() => import('./pages/ProprivoucherPage'));
const ProprivoucherDetailPage = lazy(
  () => import('./pages/proprivoucher/detail/ProprivoucherDetailPage')
);
const AsetquLandHomePage = lazy(() => import('./pages/AsetquLandHomePage'));
const AsetquLandListingPage = lazy(() => import('./pages/AsetquLandListingPage'));
const AsetquLandDetailPage = lazy(() => import('./pages/asetqu-land/detail/AsetquLandDetailPage'));
const RentConfirmationPage = lazy(() => import('./pages/asetqu-land/rent/RentConfirmationPage'));
const RentSuccessPage = lazy(() => import('./pages/asetqu-land/rent/RentSuccessPage'));
const MyAsetquLandPage = lazy(() => import('./pages/MyAsetquLandPage'));
const MyAsetquLandDetailPage = lazy(() => import('./pages/asetqu-land/my/MyAsetquLandDetailPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const ProprimarketHistoryPage = lazy(() => import('./pages/profile/ProprimarketHistoryPage'));
const PointHistoryPage = lazy(() => import('./pages/profile/PointHistoryPage'));
const RedemptionDetailPage = lazy(() => import('./pages/mobile/profile/RedemptionDetailPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

export const router = sentryCreateBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      {
        index: true,
        element: <HomePage />,
      },
      {
        path: 'login',
        element: <LoginPage />,
      },
      {
        path: 'history',
        element: <HistoryPage />,
      },
      {
        path: 'my-auctions',
        element: <MyAuctionsPage />,
      },
      {
        path: 'my-vouchers',
        element: <MyVouchersPage />,
      },
      {
        path: 'my-aset-academy',
        element: <MyAsetAcademyPage />,
      },
      {
        path: 'propribox',
        element: <PropriboxPage />,
      },
      {
        path: 'academy',
        children: [
          {
            index: true,
            element: <AcademyPageHome />,
          },
          {
            path: 'all',
            element: <AcademyPage />,
          },
          {
            path: ':id',
            element: <AcademyDetailPage />,
          },
          {
            path: ':id/resource',
            element: <AcademyResourcePage />,
          },
        ],
      },
      {
        path: 'propribid',
        children: [
          {
            index: true,
            element: <PropribidPage />,
          },
          {
            path: ':id',
            element: <PropribidDetailPage />,
          },
        ],
      },
      {
        path: 'proprimarket',
        children: [
          {
            index: true,
            element: <ProprimarketPage />,
          },
          {
            path: ':id',
            element: <ProprimarketDetailPage />,
          },
        ],
      },
      {
        path: 'proprivoucher',
        children: [
          {
            index: true,
            element: <ProprivoucherPage />,
          },
          {
            path: ':id',
            element: <ProprivoucherDetailPage />,
          },
        ],
      },
      // Left out entirely while the feature is off, so every land path falls
      // through to the catch-all and renders the 404 page.
      ...(asetquLandEnabled
        ? [
            {
              path: 'asetqu-land',
              children: [
                {
                  index: true,
                  element: <AsetquLandHomePage />,
                },
                {
                  path: 'assets',
                  element: <AsetquLandListingPage />,
                },
                {
                  path: 'assets/:id',
                  element: <AsetquLandDetailPage />,
                },
                {
                  path: 'rent/confirm',
                  element: <RentConfirmationPage />,
                },
                {
                  path: 'rent/orders/:id/confirm',
                  element: <RentConfirmationPage />,
                },
                {
                  path: 'rent/orders/:id/success',
                  element: <RentSuccessPage />,
                },
              ],
            },
            {
              path: 'my-asetqu-land',
              children: [
                {
                  index: true,
                  element: <MyAsetquLandPage />,
                },
                {
                  path: ':id',
                  element: <MyAsetquLandDetailPage />,
                },
              ],
            },
          ]
        : []),
      {
        path: 'profile',
        children: [
          {
            index: true,
            element: <ProfilePage />,
          },
          {
            path: 'proprimarket-history',
            element: <ProprimarketHistoryPage />,
          },
          {
            path: 'point-history',
            element: <PointHistoryPage />,
          },
          {
            path: 'point-history/:id',
            element: <RedemptionDetailPage />,
          },
        ],
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
]);
